import { supabase } from '../utils/supabase';
// Type reference: Database["public"]["Tables"]["analytics_events"]["Insert"]
// Event types: 'upgrade_prompt_viewed' | 'upgrade_prompt_clicked' | 'upgrade_prompt_dismissed' | 'free_tier_candidate_match_viewed'

/**
 * Analytics Service for tracking user engagement with premium features
 * Specifically tracks free-tier upgrade prompt interactions
 * 
 * @typedef {Object} AnalyticsEventData
 * @property {string} [component] - Component that triggered the event
 * @property {string} [timestamp] - ISO timestamp of event
 * @property {string} [action] - Action performed
 * @property {*} [metadata] - Additional event metadata
 */
export const analyticsService = {
  /**
   * Track when a free-tier user views the upgrade prompt
   * @param {string} userId - User ID from auth.users
   * @param {AnalyticsEventData} metadata - Event metadata
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async trackUpgradePromptView(userId, metadata = {}) {
    try {
      const { error } = await supabase
        .from('analytics_events')
        .insert([
          {
            user_id: userId,
            event_type: 'upgrade_prompt_viewed',
            event_data: {
              timestamp: new Date().toISOString(),
              ...metadata
            }
          }
        ]);

      if (error) {
        console.error('[Analytics] Error tracking prompt view:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('[Analytics] Unexpected error tracking prompt view:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Track when a free-tier user clicks the upgrade button
   */
  async trackUpgradeClick(userId, metadata = {}) {
    try {
      const { error } = await supabase
        .from('analytics_events')
        .insert([
          {
            user_id: userId,
            event_type: 'upgrade_prompt_clicked',
            event_data: {
              timestamp: new Date().toISOString(),
              action: 'view_pricing_plans',
              ...metadata
            }
          }
        ]);

      if (error) {
        console.error('[Analytics] Error tracking upgrade click:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('[Analytics] Unexpected error tracking upgrade click:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Track when a free-tier user dismisses the upgrade prompt
   */
  async trackUpgradeDismiss(userId, metadata = {}) {
    try {
      const { error } = await supabase
        .from('analytics_events')
        .insert([
          {
            user_id: userId,
            event_type: 'upgrade_prompt_dismissed',
            event_data: {
              timestamp: new Date().toISOString(),
              action: 'back_to_dashboard',
              ...metadata
            }
          }
        ]);

      if (error) {
        console.error('[Analytics] Error tracking upgrade dismiss:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('[Analytics] Unexpected error tracking upgrade dismiss:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Track when a free-tier user views a candidate match
   */
  async trackCandidateMatchViewed(userId, jobId, candidateId, metadata = {}) {
    try {
      const { error } = await supabase
        .from('analytics_events')
        .insert([
          {
            user_id: userId,
            event_type: 'free_tier_candidate_match_viewed',
            event_data: {
              job_id: jobId,
              candidate_id: candidateId,
              timestamp: new Date().toISOString(),
              ...metadata
            }
          }
        ]);

      if (error) {
        console.error('[Analytics] Error tracking candidate view:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('[Analytics] Unexpected error tracking candidate view:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Get upgrade analytics for admin dashboard
   * Returns summary of upgrade prompt interactions
   */
  async getUpgradeAnalytics(dateRange = '30d') {
    try {
      // Calculate date based on range
      let startDate = new Date();
      switch (dateRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case '1y':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        default:
          startDate.setDate(startDate.getDate() - 30);
      }

      const { data: promptViews, error: viewsError } = await supabase
        .from('analytics_events')
        .select('id, user_id, created_at')
        .eq('event_type', 'upgrade_prompt_viewed')
        .gte('created_at', startDate.toISOString());

      if (viewsError) throw viewsError;

      const { data: promptClicks, error: clicksError } = await supabase
        .from('analytics_events')
        .select('id, user_id, created_at')
        .eq('event_type', 'upgrade_prompt_clicked')
        .gte('created_at', startDate.toISOString());

      if (clicksError) throw clicksError;

      const { data: promptDismisses, error: dismissError } = await supabase
        .from('analytics_events')
        .select('id, user_id, created_at')
        .eq('event_type', 'upgrade_prompt_dismissed')
        .gte('created_at', startDate.toISOString());

      if (dismissError) throw dismissError;

      const { data: candidateViews, error: candError } = await supabase
        .from('analytics_events')
        .select('id, user_id, created_at')
        .eq('event_type', 'free_tier_candidate_match_viewed')
        .gte('created_at', startDate.toISOString());

      if (candError) throw candError;

      // Calculate metrics
      const totalPromptViews = promptViews?.length || 0;
      const totalPromptClicks = promptClicks?.length || 0;
      const totalPromptDismisses = promptDismisses?.length || 0;
      const totalCandidateViews = candidateViews?.length || 0;

      const clickThroughRate = totalPromptViews > 0
        ? ((totalPromptClicks / totalPromptViews) * 100).toFixed(2)
        : 0;

      const dismissRate = totalPromptViews > 0
        ? ((totalPromptDismisses / totalPromptViews) * 100).toFixed(2)
        : 0;

      return {
        success: true,
        data: {
          dateRange,
          startDate: startDate.toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0],
          metrics: {
            totalPromptViews,
            totalPromptClicks,
            totalPromptDismisses,
            totalCandidateViews,
            clickThroughRate: parseFloat(clickThroughRate),
            dismissRate: parseFloat(dismissRate),
            conversionRate: totalPromptViews > 0
              ? ((totalPromptClicks / totalPromptViews) * 100).toFixed(2)
              : 0
          },
          summary: {
            promptViewsPerDay: (totalPromptViews / (Math.max(1, Math.ceil((new Date() - startDate) / (1000 * 60 * 60 * 24))))).toFixed(2),
            promptClicksPerDay: (totalPromptClicks / (Math.max(1, Math.ceil((new Date() - startDate) / (1000 * 60 * 60 * 24))))).toFixed(2),
            conversionSummary: `${totalPromptClicks} users upgraded from ${totalPromptViews} views`
          }
        }
      };
    } catch (err) {
      console.error('[Analytics] Error getting upgrade analytics:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Get free-tier user engagement metrics
   * Returns breakdown of free-tier user behavior
   */
  async getFreeTierEngagement(dateRange = '30d') {
    try {
      let startDate = new Date();
      switch (dateRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case '1y':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        default:
          startDate.setDate(startDate.getDate() - 30);
      }

      // Get free-tier users who viewed upgrade prompts
      const { data: freeUsers, error: freeError } = await supabase
        .from('user_profiles')
        .select('id, email, created_at')
        .eq('subscription_tier', 'free')
        .gte('created_at', startDate.toISOString());

      if (freeError) throw freeError;

      // Get their engagement
      const { data: engagementEvents, error: engError } = await supabase
        .from('analytics_events')
        .select('user_id, event_type, created_at')
        .in('user_id', freeUsers?.map(u => u.id) || [])
        .in('event_type', ['upgrade_prompt_viewed', 'upgrade_prompt_clicked', 'free_tier_candidate_match_viewed'])
        .gte('created_at', startDate.toISOString());

      if (engError) throw engError;

      // Calculate engagement metrics
      const uniqueEngagedUsers = new Set(engagementEvents?.map(e => e.user_id) || []).size;
      const promptViewEvents = engagementEvents?.filter(e => e.event_type === 'upgrade_prompt_viewed') || [];
      const clickEvents = engagementEvents?.filter(e => e.event_type === 'upgrade_prompt_clicked') || [];
      const candViewEvents = engagementEvents?.filter(e => e.event_type === 'free_tier_candidate_match_viewed') || [];

      return {
        success: true,
        data: {
          dateRange,
          totalFreeTierUsers: freeUsers?.length || 0,
          engagedUsers: uniqueEngagedUsers,
          engagementRate: freeUsers?.length > 0
            ? ((uniqueEngagedUsers / freeUsers.length) * 100).toFixed(2)
            : 0,
          metrics: {
            promptViews: promptViewEvents.length,
            promptClicks: clickEvents.length,
            candidateViews: candViewEvents.length
          },
          conversion: {
            viewsToClicks: promptViewEvents.length > 0
              ? ((clickEvents.length / promptViewEvents.length) * 100).toFixed(2)
              : 0,
            upgradedUsers: new Set(clickEvents.map(e => e.user_id)).size
          }
        }
      };
    } catch (err) {
      console.error('[Analytics] Error getting free-tier engagement:', err);
      return { success: false, error: err.message };
    }
  }
};
